import { Globe, CircleCheck, Wifi, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const CARD_ICONS = { wifi: Wifi, pin: MapPin };

export const Internacional = () => {
  const { t } = useLang();
  const s = t.internacional;

  return (
    <section id="internacional" data-testid="international-section" className="relative py-24 sm:py-36">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="inline-flex w-16 h-16 rounded-sm border border-[#C5A059]/45 items-center justify-center mb-8">
            <Globe size={26} className="text-[#D9A94A]" strokeWidth={1.3} />
          </span>
          <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{s.overline}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#F7F7F8] leading-snug mb-7">
            {s.title1} <span className="gold-text block">{s.title2}</span>
          </h2>
          <p className="text-[15px] sm:text-base text-[#A3A3AF] leading-relaxed font-light">{s.intro}</p>
        </Reveal>

        <Reveal className="text-center mt-24">
          <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{s.coverageOverline}</p>
          <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug">{s.coverageTitle}</h3>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-3 max-w-4xl mx-auto">
          {s.regions.map(({ flag, id, title, text }, i) => (
            <Reveal key={id} delay={i * 0.12}>
              <div
                data-testid={`card-region-${id}`}
                className="h-full p-8 rounded-2xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/40 hover:-translate-y-1 transition-all duration-500 text-center"
              >
                <span className="text-4xl block mb-5">{flag}</span>
                <h4 className="font-heading text-base tracking-[0.15em] text-[#F7F7F8] uppercase mb-3">{title}</h4>
                <p className="text-sm text-[#A3A3AF] leading-relaxed font-light">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-12 max-w-2xl mx-auto text-center text-sm sm:text-[15px] text-[#6E6E7D] leading-relaxed font-light italic">
            {s.coverageNote}
          </p>
        </Reveal>

        <div className="mt-24 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <Reveal>
            <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{s.remoteOverline}</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug mb-6">{s.remoteTitle}</h3>
            <p className="text-[15px] sm:text-base text-[#A3A3AF] leading-relaxed font-light mb-9">{s.remoteIntro}</p>
            <ul data-testid="international-checklist" className="space-y-4">
              {s.remoteChecklist.map((item) => (
                <li key={item} className="flex items-center gap-3.5 text-sm sm:text-[15px] text-[#D1D1DB] font-light">
                  <CircleCheck size={19} className="text-[#D9A94A] shrink-0" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="flex flex-col gap-6">
            {s.remoteCards.map(({ icon, title, text }, i) => {
              const Icon = CARD_ICONS[icon];
              return (
                <Reveal key={icon} delay={0.1 + i * 0.12}>
                  <div
                    data-testid={`remote-card-${icon}`}
                    className="p-8 sm:p-9 rounded-2xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/40 transition-colors duration-500"
                  >
                    <div className="flex items-center gap-3.5 mb-4">
                      <Icon size={22} className="text-[#D9A94A]" strokeWidth={1.4} />
                      <h4 className="font-heading text-base tracking-[0.15em] uppercase text-[#F7F7F8]">{title}</h4>
                    </div>
                    <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.1} className="mt-24 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug mb-5">{s.finalTitle}</h3>
          <p className="max-w-xl mx-auto text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light mb-10">
            {s.finalText}
          </p>
          <a
            data-testid="international-cta-btn"
            href={whatsappLink(s.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
          >
            {s.cta} <ArrowRight size={15} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
